import { useCallback, useLayoutEffect, useState } from 'react';
import { Form, Modal, message, Radio, Input, Select, Row, Col } from 'antd';
import { OmsRecycleOrderDetail } from 'types/oms';
import { UmsMemberReceiveAddress } from 'types/ums';
import {
  addMemberReceiveAddressV2,
  updateMemberReceiveAddressV2,
} from 'apis/ums';
import { useAppSelector } from 'store/hooks';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import { getCityListByCountryCode } from 'apis/home';
import i18n from 'i18n';
import LOCALS from 'commons/locals';
import { SelectOption } from 'types/base';

interface Props {
  open: boolean;
  onClose: () => void;
  data?: OmsRecycleOrderDetail;
  getLoad: () => void;
  memberId: number;
  title?: string;
  address?: {
    id: number;
    name: string;
    phoneNumber: string;
    postCode: string;
    province: string;
    detailAddress: string;
    defaultStatus?: number;
    city?: { code: string } | string | null;
  };
}

interface UmsMemberReceiveAddress_ extends UmsMemberReceiveAddress {
  areaCode?: string;
  phone?: string;
}

const AddressAdd = ({
  open,
  onClose,
  getLoad,
  memberId,
  title,
  address,
}: Props) => {
  const [form] = Form.useForm<UmsMemberReceiveAddress_>();
  const [loading, setLoading] = useState(false);
  const { countryOptions, countryCodeOptions } =
    useAppSelector(selectGlobalInfo);
  const [cityList, setCityList] = useState<
    {
      code: string;
      countryCode: string;
      id: string;
      name: string;
    }[]
  >([]);

  const getCityList = useCallback(
    (country: string, keepCity = false) => {
      if (!keepCity) {
        form.setFieldValue('city', '');
      }
      setCityList([]);
      if (!country) return;
      getCityListByCountryCode(country).then((res) => {
        setCityList(res.data.cityList || []);
      });
    },
    [form],
  );

  useLayoutEffect(() => {
    if (!open) return;
    form.resetFields();
    setCityList([]);
    if (!address) return;
    const [areaCode, ...phoneParts] = (address.phoneNumber || '')
      .trim()
      .split(/\s+/);
    const cityCode =
      typeof address.city === 'string' ? address.city : address.city?.code;
    form.setFieldsValue({
      name: address.name,
      areaCode,
      phone: phoneParts.join(' '),
      postCode: address.postCode,
      province: address.province,
      city: cityCode,
      detailAddress: address.detailAddress,
      defaultStatus: address.defaultStatus ?? 0,
    });
    if (address.province) getCityList(address.province, true);
  }, [open, address, form, getCityList]);

  const onFinish = useCallback(() => {
    form
      .validateFields()
      .then((values) => {
        setLoading(true);
        const { areaCode, phone, city, ...rest } = values;
        const fields = {
          ...rest,
          city: city || undefined,
          phoneNumber: `${areaCode} ${phone}`,
        };
        const request = address
          ? updateMemberReceiveAddressV2(address.id, fields)
          : addMemberReceiveAddressV2({ ...fields, memberId });
        request
          .then(() => {
            message.success(i18n.t(LOCALS.successful_operation));
            onClose();
            getLoad();
          })
          .catch((e) => {
            message.error(e.message);
          })
          .finally(() => {
            setLoading(false);
          });
      })
      .catch(() => {});
  }, [address, memberId, form, getLoad, onClose]);

  const SelectFilter = useCallback(
    (input: string, option: SelectOption | undefined) =>
      (option?.label ?? '').toLowerCase().includes(input.toLowerCase()),
    [],
  );

  return (
    <Modal
      open={open}
      title={title || i18n.t(LOCALS.add)}
      onCancel={() => onClose()}
      onOk={onFinish}
      destroyOnClose
      width={700}
      confirmLoading={loading}
    >
      <Form
        form={form}
        layout="horizontal"
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 18 }}
        initialValues={{ defaultStatus: 0 }}
      >
        <Form.Item
          label={i18n.t(LOCALS.name)}
          rules={[{ required: true }]}
          name="name"
        >
          <Input placeholder={i18n.t(LOCALS.please_enter) || ''} />
        </Form.Item>
        <Form.Item
          label={
            <span>
              <span style={{ color: 'red', marginRight: 4 }}>*</span>
              {i18n.t(LOCALS.phone_number)}
            </span>
          }
          rules={[{ required: true }]}
          className="mb-0"
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item rules={[{ required: true }]} name="areaCode">
                <Select
                  options={countryCodeOptions}
                  showSearch
                  placeholder={i18n.t(LOCALS.please_select) || ''}
                />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item rules={[{ required: true }]} name="phone">
                <Input placeholder={i18n.t(LOCALS.please_enter) || ''} />
              </Form.Item>
            </Col>
          </Row>
        </Form.Item>
        <Form.Item
          label={i18n.t(LOCALS.zip_code)}
          rules={[{ required: true }]}
          name="postCode"
        >
          <Input placeholder={i18n.t(LOCALS.please_enter) || ''} />
        </Form.Item>
        <Form.Item
          label={i18n.t(LOCALS.country_region)}
          name={'province'}
          rules={[{ required: true }]}
        >
          <Select
            placeholder={i18n.t(LOCALS.country_region)}
            options={countryOptions}
            onChange={(e) => getCityList(e)}
            showSearch
            filterOption={SelectFilter}
          />
        </Form.Item>
        <Form.Item label={i18n.t(LOCALS.state_province_city)} name="city">
          <Select
            placeholder={i18n.t(LOCALS.state_province_city)}
            showSearch
            filterOption={SelectFilter}
            options={cityList.map(({ name, code }) => {
              return {
                value: code,
                label: name,
              };
            })}
          />
        </Form.Item>
        <Form.Item
          label={i18n.t(LOCALS.detail_address)}
          name="detailAddress"
          rules={[{ required: true }]}
        >
          <Input.TextArea
            rows={3}
            placeholder={i18n.t(LOCALS.detail_address) || ''}
          />
        </Form.Item>
        <Form.Item
          label={i18n.t(LOCALS.default_address)}
          name="defaultStatus"
          rules={[{ required: true }]}
        >
          <Radio.Group>
            <Radio value={1}>{i18n.t(LOCALS.yes)}</Radio>
            <Radio value={0}>{i18n.t(LOCALS.no)}</Radio>
          </Radio.Group>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddressAdd;
